import { toast } from "sonner";
import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PlatformPromptKey,
  PlatformPromptRevisionDTO,
  PromptSnapshot,
} from "@ai-crm/shared";
import { PageHeader } from "../components/layout";
import { adminApi, ApiError } from "../lib/api";
import { useAdminAuth } from "../lib/auth";

const buttonClass = "rounded-md border px-3 py-2 text-sm disabled:opacity-50";

export function SystemPromptsPage() {
  const { token } = useAdminAuth();
  const [selected, setSelected] = useState<PlatformPromptKey | null>(null);
  const [busy, setBusy] = useState(false);
  const list = useQuery({
    queryKey: ["platform-prompts", token],
    queryFn: () => adminApi.platformPrompts(token!),
    enabled: Boolean(token),
  });
  const key = selected ?? list.data?.items[0]?.key;
  return (
    <div className="space-y-4">
      <PageHeader
        title="Системные промпты"
        description="Общие инструкции платформы. Изменения применяются к следующим генерациям без перезапуска."
      />
      {list.isPending && <p role="status">Загружаем промпты...</p>}
      {list.isError && (
        <div role="alert">
          Не удалось загрузить список промптов.{" "}
          <button className={buttonClass} onClick={() => void list.refetch()}>
            Повторить
          </button>
        </div>
      )}
      {list.data?.items.length === 0 && <p>Промпты ещё не установлены.</p>}
      {list.data && list.data.items.length > 0 && (
        <label className="block space-y-2">
          <span>Инструкция</span>
          <select
            className="control"
            value={key}
            disabled={busy}
            onChange={(event) =>
              setSelected(event.target.value as PlatformPromptKey)
            }
          >
            {list.data.items.map((item) => (
              <option key={item.key} value={item.key}>
                {item.description}
              </option>
            ))}
          </select>
        </label>
      )}
      {key && token && (
        <PromptEditor
          key={key}
          promptKey={key}
          token={token}
          busy={busy}
          setBusy={setBusy}
        />
      )}
    </div>
  );
}

function PromptEditor({
  promptKey,
  token,
  busy,
  setBusy,
}: {
  promptKey: PlatformPromptKey;
  token: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
}) {
  const client = useQueryClient();
  const [opened, setOpened] = useState<PromptSnapshot | null>(null);
  const [draft, setDraft] = useState("");
  const [selectedRevision, setSelectedRevision] =
    useState<PlatformPromptRevisionDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const detailKey = ["platform-prompt", token, promptKey];
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => adminApi.platformPrompt(token, promptKey),
  });
  const history = useInfiniteQuery({
    queryKey: ["platform-prompt-revisions", token, promptKey],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      adminApi.platformPromptRevisions(token, promptKey, {
        ...(pageParam ? { cursor: pageParam } : {}),
        take: 20,
      }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  useEffect(() => {
    if (!opened && detail.data) {
      setOpened(detail.data);
      setDraft(detail.data.content);
    }
  }, [opened, detail.data]);

  function apply(snapshot: PromptSnapshot) {
    setOpened(snapshot);
    setDraft(snapshot.content);
    setConflict(false);
    setError(null);
    client.setQueryData(detailKey, snapshot);
  }

  async function reload() {
    if (busy) return;
    setBusy(true);
    try {
      apply(await adminApi.platformPrompt(token, promptKey));
    } catch {
      setError(
        "Не удалось загрузить актуальную версию. Черновик сохранён в редакторе.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function publish(revision?: PlatformPromptRevisionDTO) {
    if (!opened || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = revision
        ? await adminApi.restorePlatformPrompt(token, promptKey, {
            revisionId: revision.id,
            expectedVersion: opened.version,
          })
        : await adminApi.savePlatformPrompt(token, promptKey, {
            content: draft,
            expectedVersion: opened.version,
          });
      apply(result);
      toast.success(
        revision
          ? "Версия восстановлена новой публикацией."
          : "Промпт сохранён.",
      );
      await Promise.all([
        client.invalidateQueries({ queryKey: ["platform-prompts", token] }),
        client.invalidateQueries({
          queryKey: ["platform-prompt-revisions", token, promptKey],
        }),
      ]);
    } catch (caught) {
      const isConflict = caught instanceof ApiError && caught.status === 409;
      setConflict(isConflict);
      setError(
        isConflict
          ? "Промпт уже изменён другим администратором. Ваш черновик сохранён в редакторе."
          : "Не удалось опубликовать промпт. Черновик сохранён в редакторе.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <section className="panel space-y-4 p-4">
        <h2 className="font-semibold">Текущая инструкция</h2>
        {detail.isPending && <p role="status">Загружаем инструкцию...</p>}
        {detail.isError && <p role="alert">Не удалось загрузить инструкцию.</p>}
        {opened && (
          <>
            <p className="text-sm text-muted-foreground">
              Версия {opened.version} · ревизия {opened.revision}
            </p>
            <label className="block space-y-2">
              <span>Текст инструкции</span>
              <textarea
                className="control min-h-96 font-mono text-sm"
                value={draft}
                maxLength={100_000}
                disabled={busy}
                onChange={(event) => setDraft(event.target.value)}
              />
            </label>
            <button
              className={buttonClass}
              disabled={busy || !draft.trim() || draft.length > 100_000}
              onClick={() => void publish()}
            >
              Сохранить
            </button>
          </>
        )}
        {(conflict || detail.isError) && (
          <button
            className={buttonClass}
            disabled={busy}
            onClick={() => void reload()}
          >
            Загрузить актуальную версию
          </button>
        )}
        {conflict && (
          <p className="text-sm text-muted-foreground">
            Загрузка заменит черновик актуальным текстом без публикации.
          </p>
        )}
        {busy && <p role="status">Выполняем действие...</p>}
        {error && <p role="alert">{error}</p>}
      </section>
      <section className="panel space-y-4 p-4">
        <h2 className="font-semibold">История версий</h2>
        {history.isPending && <p role="status">Загружаем историю...</p>}
        {history.isError && (
          <div role="alert">
            Не удалось загрузить историю.{" "}
            <button
              className={buttonClass}
              disabled={busy}
              onClick={() => void history.refetch()}
            >
              Повторить загрузку истории
            </button>
          </div>
        )}
        <ul className="space-y-2">
          {history.data?.pages
            .flatMap((page) => page.items)
            .map((revision) => (
              <li key={revision.id}>
                <button
                  className={buttonClass}
                  disabled={busy}
                  onClick={() => setSelectedRevision(revision)}
                >
                  Ревизия {revision.revision} ·{" "}
                  {new Date(revision.createdAt).toLocaleString("ru-RU")}
                </button>
                <p className="mt-1 break-all text-sm text-muted-foreground">
                  Автор:{" "}
                  {revision.createdByAdminId ??
                    "Система или удалённый администратор"}
                </p>
              </li>
            ))}
        </ul>
        {history.hasNextPage && (
          <button
            className={buttonClass}
            disabled={busy || history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            Загрузить ещё
          </button>
        )}
        {selectedRevision && (
          <div className="space-y-3">
            <h3 className="font-medium">
              Просмотр ревизии {selectedRevision.revision}
            </h3>
            <p className="break-all text-sm text-muted-foreground">
              Автор:{" "}
              {selectedRevision.createdByAdminId ??
                "Система или удалённый администратор"}
            </p>
            <pre
              className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-sm"
              aria-label="Текст выбранной ревизии"
            >
              {selectedRevision.content}
            </pre>
            <button
              className={buttonClass}
              disabled={busy || !opened}
              onClick={() => void publish(selectedRevision)}
            >
              Восстановить ревизию {selectedRevision.revision}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
